import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import Card from 'app/frontend/base/components/Card'
import { hasAccessToContent } from 'app/frontend/base/components/ProtectedContent'
import Select from 'app/frontend/base/components/Select'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  useGuildMembers,
  useNeighborhoodOccupants,
  usePageInfo,
} from 'app/frontend/base/config/queries'
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import EditNoteModal from './components/EditNoteModal'
import mapStyles from './components/HousingMap.module.scss'
import NeighborhoodMap from './components/NeighborhoodMap'
import PersonalPlotChoices from './components/PersonalPlotChoices'
import PlotCharacterTable from './components/PlotCharacterTable'
import PlotConflicts from './components/PlotConflicts'
import PlotGrid from './components/PlotGrid'
import PlotModal from './components/PlotModal'
import type {
  CharacterRow,
  DisplayOccupant,
  RelevantCharacter,
} from './components/types'
import { useHousingMutations } from './hooks'
import { loadNeighborhoodMapData, MapPOI } from './utils'
import { cn } from '@/lib/utils'
import AddCharacterModal from '../TeamRoster/components/AddCharacterModal'

const HousingPage = () => {
  const { guild } = useTeam()
  const {
    data: { user },
  } = usePageInfo()
  const { data: occupancyData, isPending } = useNeighborhoodOccupants(guild.id)
  const { data: guildMembers = [] } = useGuildMembers(guild.id)
  const {
    createChoice,
    updateChoice,
    deleteChoice,
    isPending: mutationPending,
  } = useHousingMutations(guild.id)

  const [mapId, setMapId] = useState(1)
  const [shardIndex, setShardIndex] = useState(0)
  const [selectedPlot, setSelectedPlot] = useState<MapPOI | null>(null)
  const [hoveringPlot, setHoveringPlot] = useState<string | null>(null)
  const [addingCharacters, setAddingCharacters] = useState(false)
  const [personalNoteChoice, setPersonalNoteChoice] =
    useState<DisplayOccupant | null>(null)

  const neighborhoodType =
    guild.settings.teamNeighborhoodType.value === 'charter'
      ? 'charter'
      : 'guild'
  const effectiveShardIndex = neighborhoodType === 'charter' ? 0 : shardIndex
  const isAdmin = hasAccessToContent(
    'admin',
    'base',
    guild,
    undefined,
    true,
  ).hasAccess
  const mapData = loadNeighborhoodMapData(mapId)

  useEffect(() => {
    if (occupancyData?.main_map_id) {
      setMapId(occupancyData.main_map_id)
    }
  }, [occupancyData?.main_map_id])

  const plots = useMemo(
    () =>
      [...mapData.Content.POIs]
        .filter((poi) => poi.Type === 'Plot')
        .sort((a, b) => parseInt(a.Name) - parseInt(b.Name)),
    [mapData],
  )

  const guildMemberById = useMemo(() => {
    return guildMembers.reduce<Record<number, wowaudit.CharacterReference>>(
      (acc, character) => {
        acc[character.id] = character
        return acc
      },
      {},
    )
  }, [guildMembers])

  const userCharacterById = useMemo(() => {
    return (user?.characterReferences ?? []).reduce<
      Record<number, wowaudit.CharacterReference>
    >((acc, character) => {
      acc[character.id] = character
      return acc
    }, {})
  }, [user?.characterReferences])

  const baseCharacterById = useMemo(() => {
    return { ...guildMemberById, ...userCharacterById }
  }, [guildMemberById, userCharacterById])

  const relevantCharacters = useMemo(() => {
    const charactersById = new Map<number, RelevantCharacter>()
    const mapOccupants = (occupancyData?.occupants ?? []).filter(
      (occupant) =>
        occupant.map_id === mapId &&
        (neighborhoodType !== 'charter' || occupant.shard_index === 0),
    )
    const occupantUserIds = new Set(
      mapOccupants
        .map((occupant) => occupant.user_id)
        .filter((userId): userId is number => !!userId),
    )

    ;(occupancyData?.users ?? []).forEach((occupancyUser) => {
      if (!occupantUserIds.has(occupancyUser.id)) return

      const character =
        (occupancyUser.main_character_id
          ? baseCharacterById[occupancyUser.main_character_id]
          : undefined) ??
        occupancyUser.character_ids
          .map((id) => baseCharacterById[id])
          .find(Boolean)

      charactersById.set(character?.id ?? -occupancyUser.id, {
        id: character?.id ?? -occupancyUser.id,
        rowKey: character
          ? `character-${character.id}`
          : `user-${occupancyUser.id}`,
        name: character?.name ?? occupancyUser.name,
        characterClass: character?.characterClass,
        userId: occupancyUser.id,
        profileId: character?.profileId,
        realmName: character?.realm.name,
        rankInGuild: character?.rankInGuild,
        inTeam: character ? !!guildMemberById[character.id] : false,
        isMain: character?.id === occupancyUser.main_character_id,
        userHasApprovedPlot:
          occupancyUser.approved_plot_on_maps.includes(mapId),
        userHasClaimedPlot: occupancyUser.claimed_plot_on_maps.includes(mapId),
      })
    })
    mapOccupants
      .filter((occupant) => occupant.character_id)
      .forEach((occupant) => {
        if (!occupant.character_id || charactersById.has(occupant.character_id))
          return

        const character = baseCharacterById[occupant.character_id]
        if (!character) return

        charactersById.set(character.id, {
          id: character.id,
          rowKey: `character-${character.id}`,
          name: character.name,
          characterClass: character.characterClass,
          profileId: character.profileId,
          realmName: character.realm.name,
          rankInGuild: character.rankInGuild,
          inTeam: false,
          userHasApprovedPlot: false,
          userHasClaimedPlot: false,
        })
      })

    return [...charactersById.values()].sort((a, b) => {
      if (a.isMain && !b.isMain) return -1
      if (!a.isMain && b.isMain) return 1
      if (a.rankInGuild !== undefined && b.rankInGuild !== undefined)
        return a.rankInGuild - b.rankInGuild
      if (a.rankInGuild !== undefined) return -1
      if (b.rankInGuild !== undefined) return 1
      return a.name.localeCompare(b.name)
    })
  }, [
    baseCharacterById,
    guildMemberById,
    mapId,
    occupancyData?.occupants,
    occupancyData?.users,
  ])

  const relevantCharacterById = useMemo(() => {
    return relevantCharacters.reduce<Record<number, RelevantCharacter>>(
      (acc, character) => {
        if (character.id > 0) acc[character.id] = character
        return acc
      },
      {},
    )
  }, [relevantCharacters])

  const relevantCharacterByUserId = useMemo(() => {
    return relevantCharacters.reduce<Record<number, RelevantCharacter>>(
      (acc, character) => {
        if (character.userId) acc[character.userId] = character
        return acc
      },
      {},
    )
  }, [relevantCharacters])

  const displayOccupants = useMemo(() => {
    return (occupancyData?.occupants ?? []).flatMap((occupant) => {
      if (occupant.map_id !== mapId) return []
      if (neighborhoodType === 'charter' && occupant.shard_index !== 0)
        return []

      const character = occupant.user_id
        ? relevantCharacterByUserId[occupant.user_id]
        : occupant.character_id
        ? relevantCharacterById[occupant.character_id]
        : undefined

      return character ? [{ ...occupant, character }] : []
    })
  }, [
    mapId,
    neighborhoodType,
    occupancyData?.occupants,
    relevantCharacterById,
    relevantCharacterByUserId,
  ])

  const occupantsByPlot = useMemo(() => {
    return displayOccupants.reduce<Record<string, DisplayOccupant[]>>(
      (acc, occupant) => {
        const key = occupant.plot_id.toString()
        acc[key] = [...(acc[key] ?? []), occupant]
        return acc
      },
      {},
    )
  }, [displayOccupants])

  const occupantsByCharacter = useMemo(() => {
    const grouped = displayOccupants.reduce<Record<string, DisplayOccupant[]>>(
      (acc, occupant) => {
        acc[occupant.character.rowKey] = [
          ...(acc[occupant.character.rowKey] ?? []),
          occupant,
        ]
        return acc
      },
      {},
    )

    return relevantCharacters.reduce<Record<string, DisplayOccupant[]>>(
      (acc, character) => {
        acc[character.rowKey] = grouped[character.rowKey] ?? []
        return acc
      },
      {},
    )
  }, [displayOccupants, relevantCharacters])

  const characterForUser = useMemo(() => {
    if (!user) return undefined

    const userCharacter = user.characterReferences
      .map((character) => baseCharacterById[character.id] ?? character)
      .find(
        (character) =>
          neighborhoodType === 'charter' || !!guildMemberById[character.id],
      )

    return (
      relevantCharacters.find(
        (character) =>
          character.userId === user.id &&
          character.characterClass &&
          character.inTeam,
      ) ??
      relevantCharacters.find(
        (character) => character.userId === user.id && character.characterClass,
      ) ??
      user.characterReferences
        .map((character) => relevantCharacterById[character.id])
        .find(Boolean) ??
      (userCharacter
        ? {
            id: userCharacter.id,
            rowKey: `character-${userCharacter.id}`,
            name: userCharacter.name,
            characterClass: userCharacter.characterClass,
            userId: user.id,
            profileId: userCharacter.profileId,
            realmName: userCharacter.realm.name,
            rankInGuild: userCharacter.rankInGuild,
            inTeam: !!guildMemberById[userCharacter.id],
            isMain: userCharacter.main,
            userHasApprovedPlot: false,
            userHasClaimedPlot: false,
          }
        : undefined)
    )
  }, [
    baseCharacterById,
    guildMemberById,
    neighborhoodType,
    relevantCharacterById,
    relevantCharacters,
    user,
  ])

  const occupantsForUser = useMemo(() => {
    if (!characterForUser) return []
    return occupantsByCharacter[characterForUser.rowKey] ?? []
  }, [characterForUser, occupantsByCharacter])

  const currentUserChoices = useMemo(() => {
    return occupantsForUser
      .filter((occupant) => occupant.ranked_choice > 0)
      .sort((a, b) => a.ranked_choice - b.ranked_choice)
  }, [occupantsForUser])

  const selectedPlotOccupants = selectedPlot
    ? occupantsByPlot[selectedPlot.Name] ?? []
    : []
  const selectedPlotUserChoice = selectedPlotOccupants.find(
    (occupant) => user && occupant.character.userId === user.id,
  )
  const userHasApprovedPlot = occupantsForUser.some(
    (occupant) => occupant.status === 'approved',
  )

  const plotSummary = useMemo(() => {
    return plots.reduce(
      (summary, plot) => {
        const occupants = (occupantsByPlot[plot.Name] ?? []).filter(
          (occupant) => occupant.shard_index === effectiveShardIndex,
        )
        if (
          occupants.some((occupant) =>
            ['approved', 'claimed'].includes(occupant.status),
          )
        ) {
          summary.approved += 1
        } else if (
          occupants.some(
            (occupant) =>
              occupant.status === 'requested' && occupant.ranked_choice === 1,
          )
        ) {
          summary.requested += 1
        } else {
          summary.vacant += 1
        }

        return summary
      },
      { vacant: 0, requested: 0, approved: 0 },
    )
  }, [effectiveShardIndex, occupantsByPlot, plots])

  const characterRows = useMemo<CharacterRow[]>(() => {
    return relevantCharacters.map((character) => {
      const choices = (occupantsByCharacter[character.rowKey] ?? []).reduce<
        Record<number, DisplayOccupant | undefined>
      >((acc, occupant) => {
        acc[occupant.ranked_choice] = occupant
        return acc
      }, {})

      return { character, choices }
    })
  }, [occupantsByCharacter, relevantCharacters])

  const trackedCharacterIds = useMemo(() => {
    const userIdsInTable = new Set(
      relevantCharacters
        .map((character) => character.userId)
        .filter((userId): userId is number => userId !== undefined),
    )
    const characterIdsForTrackedUsers = new Set(
      (occupancyData?.users ?? [])
        .filter((targetUser) => userIdsInTable.has(targetUser.id))
        .flatMap((targetUser) => targetUser.character_ids),
    )

    return new Set([
      ...relevantCharacters
        .map((character) => character.id)
        .filter((id) => id > 0),
      ...characterIdsForTrackedUsers,
    ])
  }, [occupancyData?.users, relevantCharacters])

  const requestPlot = (
    rankedChoice: number,
    plotId: string | number | undefined = selectedPlot?.Name,
    character?: RelevantCharacter,
  ) => {
    if (!plotId) return

    createChoice.mutate({
      map_id: mapId,
      plot_id: plotId,
      ranked_choice: rankedChoice,
      shard_index: effectiveShardIndex,
      character_id:
        isAdmin && character && character.userId === undefined
          ? character.id
          : undefined,
      user_id: isAdmin && character?.userId ? character.userId : undefined,
    })
  }

  const withdrawChoice = (
    rankedChoice: number,
    character?: RelevantCharacter,
  ) => {
    deleteChoice.mutate({
      rankedChoice,
      payload: {
        map_id: mapId,
        character_id:
          isAdmin && character && character.userId === undefined
            ? character.id
            : undefined,
        user_id: isAdmin && character?.userId ? character.userId : undefined,
      },
    })
  }

  const addCharacter = (characterId: number) => {
    createChoice.mutate({
      map_id: mapId,
      plot_id: 999,
      ranked_choice: 4,
      shard_index: effectiveShardIndex,
      character_id: characterId,
    })
  }

  if (isPending) {
    return (
      <div className='flex flex-col gap-4 mt-48'>
        <div className='grid gap-3 md:grid-cols-3'>
          {[1, 2, 3].map((rankedChoice) => (
            <Skeleton key={rankedChoice} className='h-56' />
          ))}
        </div>
        <Skeleton className='h-[34rem] w-full' />
      </div>
    )
  }

  return (
    <div>
      <div className='mb-4 flex flex-col gap-3'>
        <div className='flex flex-wrap items-end justify-between gap-3'>
          <div>
            <div className='text-sm text-muted-foreground'>
              {guild.neighborhoodName ? 'Neighborhood' : 'Housing'}
            </div>
            <h2 className='mb-0'>{guild.neighborhoodName ?? 'Neighborhood'}</h2>
          </div>

          <div className='flex flex-wrap gap-2'>
            {neighborhoodType === 'guild' ? (
              <Card className='flex items-center gap-4 px-3 py-2'>
                <button
                  type='button'
                  aria-label='Previous subdivision'
                  disabled={effectiveShardIndex === 0}
                  className='text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30'
                  onClick={() =>
                    setShardIndex((value) => Math.max(0, value - 1))
                  }
                >
                  <ChevronLeft className='h-5 w-5' />
                </button>
                <span className='whitespace-nowrap'>
                  Subdivision {effectiveShardIndex + 1}
                </span>
                <button
                  type='button'
                  aria-label='Next subdivision'
                  className='text-muted-foreground transition-colors hover:text-foreground'
                  onClick={() => setShardIndex((value) => value + 1)}
                >
                  <ChevronRight className='h-5 w-5' />
                </button>
              </Card>
            ) : (
              <Badge variant='outline'>Charter neighborhood</Badge>
            )}

            <Select
              value={mapId.toString()}
              setValue={(value) => setMapId(value ? parseInt(value) : 1)}
              options={[1, 2].map((targetMapId) => ({
                value: targetMapId.toString(),
                label: loadNeighborhoodMapData(targetMapId).Content.Name,
              }))}
              className='w-32'
            />
          </div>
        </div>

        <div className='flex flex-wrap items-start justify-between gap-4'>
          <div className='flex min-w-0 flex-1 flex-col gap-3'>
            <p className='text-sm text-muted-foreground'>
              On this page you can plan the plot assignments for your
              guild&apos;s neighborhood. Any user with a character in the guild
              can request their favorite plots here
              {isAdmin ? ', and admins can approve or deny requests.' : '.'}
            </p>

            {occupantsForUser.some(
              (occupant) => occupant.status === 'claimed',
            ) ? (
              <Alert variant='success' className='py-2 w-fit'>
                <AlertDescription className='flex items-center gap-2'>
                  <CheckCircle className='h-4 w-4' />
                  You have claimed plot{' '}
                  {
                    occupantsForUser.find(
                      (occupant) => occupant.status === 'claimed',
                    )?.plot_id
                  }{' '}
                  in-game
                </AlertDescription>
              </Alert>
            ) : null}
          </div>

          <Card className='min-w-28 shrink-0 self-start py-1'>
            <SummaryItem label='available' value={plotSummary.vacant} />
            <SummaryItem
              label='requested'
              value={plotSummary.requested}
              className='text-warning-foreground'
            />
            <SummaryItem
              label='approved'
              value={plotSummary.approved}
              className='text-success-foreground'
            />
          </Card>
        </div>
      </div>

      {!user ? (
        <Alert variant='warning'>
          <AlertTitle>Log in to request a plot</AlertTitle>
          <AlertDescription>
            You can browse the neighborhood, but plot requests require a linked
            Battle.net account.
          </AlertDescription>
        </Alert>
      ) : null}

      {user && !characterForUser ? (
        <Alert variant='warning'>
          <AlertTitle>No matching character found</AlertTitle>
          <AlertDescription>
            You do not seem to own any characters
            {neighborhoodType === 'guild' ? ' in this guild' : ' on this team'}.
            Visit your <Link to={`${guild.path}/profile`}>profile</Link> and
            update your list of characters.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className='mb-4'>
        <PersonalPlotChoices
          choices={currentUserChoices}
          mapId={mapId}
          plots={plots}
          occupantsByPlot={occupantsByPlot}
          shardIndex={effectiveShardIndex}
          disabled={!user || !characterForUser}
          mutationPending={mutationPending}
          userHasApprovedPlot={userHasApprovedPlot}
          onRequest={(rankedChoice, plotId) =>
            requestPlot(rankedChoice, plotId)
          }
          onWithdraw={(rankedChoice) => withdrawChoice(rankedChoice)}
          onOpenPlot={(plot) => setSelectedPlot(plot)}
          onEditNote={setPersonalNoteChoice}
        />
      </div>

      <div className={cn(mapStyles.mapContainer, 'mb-4 border rounded-md')}>
        <NeighborhoodMap
          key={mapId}
          mapId={mapId}
          neighborhood={mapData.Content}
          openPlot={selectedPlot}
          occupantsByPlot={occupantsByPlot}
          user={user}
          userHasApprovedPlot={userHasApprovedPlot}
          userHasClaimedPlot={occupantsForUser.some(
            (occupant) => occupant.status === 'claimed',
          )}
          shardIndex={effectiveShardIndex}
          hoveringPlot={hoveringPlot}
          setHoveringPlot={setHoveringPlot}
          onOpenPlot={setSelectedPlot}
        />

        <PlotGrid
          mapId={mapId}
          neighborhood={mapData.Content}
          occupantsByPlot={occupantsByPlot}
          shardIndex={effectiveShardIndex}
          hoveringPlot={hoveringPlot}
          setHoveringPlot={setHoveringPlot}
          onOpenPlot={setSelectedPlot}
        />
      </div>

      <div className='mb-4'>
        <PlotConflicts
          occupantsByPlot={occupantsByPlot}
          shardIndex={effectiveShardIndex}
          neighborhoodType={neighborhoodType}
        />
      </div>

      {isAdmin ? (
        <div className='mb-1 flex justify-end'>
          <Button variant='outline' onClick={() => setAddingCharacters(true)}>
            Add characters
          </Button>
        </div>
      ) : null}

      <PlotCharacterTable
        rows={characterRows}
        plots={plots}
        occupantsByPlot={occupantsByPlot}
        shardIndex={effectiveShardIndex}
        isAdmin={isAdmin}
        mutationPending={mutationPending}
        onOpenPlot={(plot) => setSelectedPlot(plot)}
        onSetShardIndex={setShardIndex}
        onRequest={requestPlot}
        onWithdraw={withdrawChoice}
        onStatusChange={(occupantId, status) =>
          updateChoice.mutate({ occupantId, payload: { status } })
        }
      />

      <PlotModal
        plot={selectedPlot}
        plots={plots}
        neighborhood={mapData.Content}
        mapId={mapId}
        videoUrl={mapData.Content.VideoURL}
        plotOccupants={selectedPlotOccupants}
        currentUserChoices={currentUserChoices}
        selectedPlotUserChoice={selectedPlotUserChoice}
        shardIndex={effectiveShardIndex}
        isAdmin={isAdmin}
        userLoggedIn={!!user}
        mutationPending={mutationPending}
        userHasApprovedPlot={userHasApprovedPlot}
        onClose={() => setSelectedPlot(null)}
        onOpenPlot={setSelectedPlot}
        onRequest={requestPlot}
        onWithdraw={withdrawChoice}
        onSaveNote={(occupantId, value) =>
          updateChoice.mutate({
            occupantId,
            payload: { note: value },
          })
        }
        onStatusChange={(occupantId, status) =>
          updateChoice.mutate({ occupantId, payload: { status } })
        }
      />

      {isAdmin && addingCharacters && occupancyData ? (
        <AddCharacterModal
          open={addingCharacters}
          setOpen={setAddingCharacters}
          title='Add characters'
          guildOnly
          simpleAdd
          addLabel='Add'
          existingCharacterIds={trackedCharacterIds}
          onAddCharacter={({ characterId }) => addCharacter(characterId)}
          isAddingAny={createChoice.isPending}
          addingCharacterId={createChoice.variables?.character_id ?? undefined}
          introContent={
            <div className='space-y-4'>
              <p>
                The table on this page always displays the characters from your
                team&apos;s roster. Other guild members will appear
                automatically once they request a plot,{' '}
                <strong>they do not need to be in the table beforehand.</strong>
              </p>

              {neighborhoodType === 'guild' ? (
                <p>
                  If you still want to add guild members manually, you can do so
                  here.{' '}
                  <em>
                    When you add multiple characters belonging to the same user,
                    all but one (the one with the highest guild rank) will be
                    removed automatically as soon as the user requests a plot.
                  </em>
                </p>
              ) : (
                <Alert>
                  <AlertDescription>
                    Only characters from your guild can be added manually here.
                    For technical reasons, characters from outside the guild
                    need to be on your team&apos;s roster before they can
                    request a plot.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          }
        />
      ) : null}

      <EditNoteModal
        occupant={personalNoteChoice}
        mutationPending={mutationPending}
        onClose={() => setPersonalNoteChoice(null)}
        onSave={(occupantId, value) =>
          updateChoice.mutate({
            occupantId,
            payload: { note: value },
          })
        }
      />
    </div>
  )
}

const SummaryItem = ({
  label,
  value,
  className,
}: {
  label: string
  value: number
  className?: string
}) => {
  return (
    <div className='flex items-baseline justify-between gap-3 text-sm'>
      <span className={`text-lg font-semibold ${className ?? ''}`}>
        {value}
      </span>
      <span className='text-muted-foreground'>{label}</span>
    </div>
  )
}

export default HousingPage
